import styled from "styled-components";
import { ate, ALVO_DE_TOQUE } from "../../global/breakpoints";

// ===== INTERFACE DO TEMA =====
interface Theme {
  colors: {
    background: string;
    text: string;
    selection: string;
    modalColor: string;
    border: string;
    primary?: string;
    danger?: string;
    selectionDark?: string;
    textOnPrimary?: string;
    label?: string;
    borderColor?: string;
  };
}

// ===== SESSION CONFIGURATION =====
export const SessionConfigurationContainer = styled.div`
  flex-grow: 1;
  max-width: 100%;
  /* min-width:0 é o que permite este filho ENCOLHER: sem ele o flex usa a
     largura do conteúdo (a tabela) como mínimo, a caixa estoura para a direita
     e o menu da esquerda é coberto — que é exatamente o que cortava os rótulos. */
  min-width: 0;
  overflow-y: auto;
  overflow-x: auto;
  box-sizing: border-box;
  position: relative;

  & > * {
    max-height: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

// ===== COMPONENTES DE CONFIGURAÇÃO PRINCIPAIS =====
export const ConfigurationContainer = styled.div`
  opacity: 1;
  z-index: 2;
`;

/*
 * A CAIXA DA CONFIGURAÇÃO.
 *
 * Tinha `height: calc(100vh - 200u)` — altura FIXA amarrada à janela. Junto com
 * o `height: calc(100% - 80u)` do ConfigurationContent, isso fazia toda tela de
 * Configuração ter exatamente a altura do monitor e CORTAR o que passasse:
 * numa janela baixa, a lista de SKUs perdia as últimas linhas e a paginação.
 * `min-height` mantém o mesmo visual quando o conteúdo é curto e deixa crescer
 * quando é longo.
 */
export const Card = styled.div`
  background-color: ${(props) => props.theme.colors.background + "cc"};
  color: ${(props) => props.theme.colors.text};
  border: 1px solid ${(props) => props.theme.colors.text};
  padding: var(--sp-10);
  margin: var(--sp-10);
  border-radius: 8px;
  min-height: calc(100vh - calc(var(--u) * 200));
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${ate.tablet} {
    padding: var(--sp-8);
    margin: var(--sp-4);
  }
`;

export const Icon = styled.div`
  font-family: "Material Icons";
  text-align: center;
  font-size: calc(var(--u) * 45);

  ${ate.tablet} {
    font-size: calc(var(--u) * 30);
  }
  color: ${(props) => props.theme.colors.selection};
  text-shadow: -0.5px -0.5px 0.5px black, 0.5px -0.5px 0.5px black,
    -0.5px 0.5px 0.5px black, 0.5px 0.5px 0.5px black;
`;

export const Title = styled.h1`
  font-size: calc(var(--u) * 45);
  margin: var(--sp-8) 0;

  ${ate.tablet} {
    font-size: calc(var(--u) * 30);
  }
  text-align: center;
  color: ${(props) => props.theme.colors.selection};
  text-shadow: -0.5px -0.5px 0.5px black, 0.5px -0.5px 0.5px black,
    -0.5px 0.5px 0.5px black, 0.5px 0.5px 0.5px black;
`;

// ===== COMPONENTES DE NAVEGAÇÃO =====
/*
 * AS SETE ABAS DE CIMA.
 *
 * Eram `justify-content: space-around` sem quebra, e a aba SELECIONADA crescia
 * de fs-xl para fs-2xl. Duas consequências: as sete não cabiam na largura útil
 * (a última, "Avançado", saía cortada pela borda do cartão), e cada clique
 * mudava a largura do item escolhido — a fila inteira pulava de lugar debaixo
 * do cursor.
 *
 * Agora: quebra de linha permitida, e o destaque é peso + sublinhado, sem
 * mexer no tamanho. O `scroll-snap` só entra no telefone, onde a fila vira uma
 * faixa que rola de lado.
 */
export const HorizontalOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--sp-4) var(--sp-8);
  padding: var(--sp-8) 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.text};

  ${ate.telefone} {
    flex-wrap: nowrap;
    overflow-x: auto;
    justify-content: flex-start;
    scroll-snap-type: x proximity;

    &::-webkit-scrollbar {
      display: none;
    }

    /*
     * O DESBOTADO DA DIREITA, que e' o unico aviso de que a fila continua.
     *
     * Medido em 375px: 838px de abas dentro de 318px visiveis, CINCO das oito
     * fora da tela. Como a barra de rolagem esta escondida logo acima, nao
     * sobrava nenhum sinal de que havia mais — a fila parecia terminar em
     * "Cardapio".
     *
     * A mascara apaga os ultimos ~24px da faixa. Ela e' so' o AVISO: quem
     * chega pelo teclado nao depende dela, porque a seta ja' anda entre as
     * abas e a aba ativa e' trazida para a vista.
     */
    mask-image: linear-gradient(
      to right,
      black calc(100% - var(--sp-24)),
      transparent 100%
    );
  }
`;

export const HorizontalOptionItem = styled.div<{ isSelected: boolean }>`
  cursor: pointer;
  /* Tamanho ÚNICO: o destaque é peso e sublinhado. Ver o comentário acima. */
  font-size: var(--fs-lg);
  line-height: 1.15;
  padding: var(--sp-8) var(--sp-6);
  min-height: ${ALVO_DE_TOQUE};
  display: flex;
  align-items: center;
  text-align: center;
  white-space: nowrap;
  scroll-snap-align: start;
  color: ${(props) =>
    props.isSelected ? props.theme.colors.selection : props.theme.colors.text};
  font-weight: ${(props) => (props.isSelected ? "bold" : "normal")};
  border-bottom: ${(props) =>
    props.isSelected ? `2px solid ${props.theme.colors.selection}` : "none"};

  ${(props) =>
    props.isSelected &&
    `
    text-shadow:
      -1px -1px 0 ${props.theme.colors.text},
      1px -1px 0 ${props.theme.colors.text},
      -1px 1px 0 ${props.theme.colors.text},
      1px 1px 0 ${props.theme.colors.text};
  `}

  &:hover {
    color: ${(props) => props.theme.colors.selection};
  }
`;

export const ConfigurationContent = styled.div`
  display: flex;
  gap: var(--sp-12);
  margin-top: var(--sp-16);
  flex: 1;
  min-height: 0;
  min-width: 0;

  /* Abaixo de laptop o menu lateral da aba vira uma faixa em cima: com ~250 de
     menu mais a tabela, sobrava largura para nenhum dos dois. */
  ${ate.laptop} {
    flex-direction: column;
  }
`;

/*
 * O MENU DA ABA (SKU, Categorias, Grupos, Fornecedores…).
 *
 * Tinha largura fixa de 250 e nada que impedisse o texto de vazar: na tela do
 * Guilherme aparecia "Categoric", "Fornecedo", "Tipos de Moviment" — os
 * rótulos cortados no meio da palavra pela tabela que vinha ao lado.
 *
 * Agora ele NÃO encolhe abaixo do necessário (`flex: 0 0 auto` com largura
 * mínima) e o texto quebra em vez de ser cortado. Abaixo de laptop ele deita e
 * vira uma faixa rolável, que é o que devolve a largura inteira para a tabela.
 */
export const VerticalOptions = styled.div`
  flex: 0 0 calc(var(--u) * 210);
  max-width: calc(var(--u) * 210);
  padding-right: var(--sp-10);
  overflow-y: auto;
  box-sizing: border-box;

  ${ate.laptop} {
    flex: 0 0 auto;
    max-width: 100%;
    display: flex;
    gap: var(--sp-6);
    overflow-x: auto;
    padding: 0 0 var(--sp-8);
    border-bottom: 1px solid ${(props) => props.theme.colors.text}33;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const VerticalOptionItem = styled.div<{ isSelected: boolean }>`
  cursor: pointer;
  /* Tamanho único, pelo mesmo motivo das abas de cima: item que cresce ao ser
     escolhido faz a lista inteira pular. */
  font-size: var(--fs-md);
  line-height: 1.25;
  padding: var(--sp-8) var(--sp-10);
  min-height: ${ALVO_DE_TOQUE};
  display: flex;
  align-items: center;
  /* Quebra, não corta. */
  white-space: normal;
  overflow-wrap: anywhere;

  ${ate.laptop} {
    white-space: nowrap;
    border-left: none !important;
    border-radius: 6px;
  }
  color: ${(props) =>
    props.isSelected ? props.theme.colors.selection : props.theme.colors.text};
  font-weight: ${(props) => (props.isSelected ? "bold" : "normal")};
  border-left: ${(props) =>
    props.isSelected ? `4px solid ${props.theme.colors.selection}` : "none"};

  ${(props) =>
    props.isSelected &&
    `
    text-shadow:
      -1px -1px 0 ${props.theme.colors.text},
      1px -1px 0 ${props.theme.colors.text},
      -1px 1px 0 ${props.theme.colors.text},
      1px 1px 0 ${props.theme.colors.text};
  `}

  &:hover {
    color: ${(props) => props.theme.colors.selection};
  }
`;
